import React, { useEffect, useState, useCallback } from "react";
import { Sparkle, X, Plus } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const C = {
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  red: "#F87171",
  ink: "#0A2826",
};

const THRESHOLD = 5;
const DISMISS_KEY = "clinq:low-credit-nudge:dismissed-at-balance";

/**
 * Bottom-right pill nudging users to top up when their credit balance is low.
 * Sits ~80px above the Ask Clinq AI FAB so both remain tappable.
 *
 * - Shows only when balance ≤ 5.
 * - Zero-balance state uses a stronger red tint.
 * - Dismissible for the session; re-appears if the balance drops further
 *   (so the nudge doesn't get stale after one dismissal).
 * - Refreshes on the shared `clinq:credits-changed` event dispatched by
 *   PitchEditor, WizardSlidePreviewAndNav, ImproveSlideDesign, etc.
 */
export default function LowCreditNudge() {
  const [balance, setBalance] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(() => {
    try {
      const raw = sessionStorage.getItem(DISMISS_KEY);
      return raw === null ? null : Number(raw);
    } catch {
      return null;
    }
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/credits/me");
      setBalance(Number(data?.balance ?? 0));
    } catch {
      /* silent — the chip in the header will surface any issue */
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChanged = () => refresh();
    window.addEventListener("clinq:credits-changed", onChanged);
    return () => window.removeEventListener("clinq:credits-changed", onChanged);
  }, [refresh]);

  if (balance === null) return null;
  if (balance > THRESHOLD) return null;
  // Suppress if dismissed at a balance >= current balance (i.e. balance
  // hasn't dropped further since the last dismissal).
  if (dismissedAt !== null && dismissedAt <= balance) return null;

  const isEmpty = balance === 0;
  const accent = isEmpty ? C.red : C.orange;
  const label = isEmpty
    ? "You're out of credits"
    : `Only ${balance} credit${balance === 1 ? "" : "s"} left`;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, String(balance));
    } catch {
      /* private mode — dismissal just won't persist, no harm */
    }
    setDismissedAt(balance);
  };

  return (
    <div
      data-testid="low-credit-nudge"
      role="status"
      aria-live="polite"
      className="fixed right-6 z-40 flex items-center gap-2.5 rounded-full pl-4 pr-2 py-2 shadow-2xl"
      style={{
        // ~72px above the Ask Clinq AI FAB (which sits at bottom-6 = 24px + ~48px tall).
        bottom: "88px",
        backgroundColor: C.ink,
        border: `1px solid ${accent}55`,
        boxShadow: `0 12px 32px rgba(0,0,0,0.45), 0 0 0 1px ${accent}22 inset`,
        fontFamily: "Manrope, sans-serif",
        maxWidth: "min(92vw, 380px)",
      }}
    >
      <span
        aria-hidden="true"
        className="relative flex h-2 w-2 shrink-0"
      >
        <span
          className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
          style={{ backgroundColor: accent }}
        />
        <span
          className="relative inline-flex h-2 w-2 rounded-full"
          style={{ backgroundColor: accent }}
        />
      </span>

      <Sparkle size={14} weight="fill" style={{ color: C.tealSoft }} aria-hidden="true" />

      <span
        className="text-xs font-semibold whitespace-nowrap"
        style={{ color: "#fff" }}
        data-testid="low-credit-nudge-label"
      >
        {label}
      </span>

      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent("clinq:topup", { detail: {} }))}
        data-testid="low-credit-nudge-buy"
        className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-[0.14em] rounded-full px-3 py-1.5 transition-transform hover:scale-105"
        style={{
          backgroundColor: accent,
          color: C.ink,
        }}
      >
        <Plus size={10} weight="bold" /> Buy credits
      </button>

      <button
        type="button"
        onClick={dismiss}
        data-testid="low-credit-nudge-dismiss"
        aria-label="Dismiss low credits notice"
        className="w-6 h-6 grid place-items-center rounded-full transition-colors hover:bg-white/10"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}
